import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";
import { Loader2, RefreshCw } from "lucide-react";

interface LocationState {
    identifier?: string;
    type?: "email" | "phone";
}

export default function CandidateVerify() {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [message, setMessage] = useState("");
    const location = useLocation();
    const navigate = useNavigate();

    // 🧩 Extract login info from route or localStorage
    const { identifier, type } = (location.state || {}) as LocationState;
    const storedEmail = localStorage.getItem("candidateEmail");
    const finalIdentifier = identifier || storedEmail || "";
    const loginType: "email" | "phone" = type || "email";

    // 🔁 RESEND CODE
    async function handleResend() {
        setResending(true);
        setMessage("");

        try {
            if (!finalIdentifier) throw new Error("Missing email or phone.");

            console.log("🔁 Resending OTP to:", finalIdentifier);

            const { error } =
                loginType === "phone"
                    ? await supabase.auth.signInWithOtp({
                        phone: finalIdentifier,
                        options: { shouldCreateUser: false },
                    })
                    : await supabase.auth.signInWithOtp({
                        email: finalIdentifier,
                        options: { shouldCreateUser: false },
                    });

            if (error) throw error;
            setMessage("📧 A new 6-digit code was sent to your email.");
        } catch (err: any) {
            console.error("❌ Resend error:", err);
            setMessage("⚠️ Failed to resend code: " + (err.message || ""));
        }

        // Prevent spam clicks — re-enable after 15 seconds
        setTimeout(() => setResending(false), 15000);
    }

    // ✅ VERIFY CODE
    async function handleVerify(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            if (!finalIdentifier) throw new Error("Missing email or phone.");

            console.log("🔐 Verifying OTP for:", finalIdentifier);

            const { data: verifyData, error: otpError } =
                loginType === "phone"
                    ? await supabase.auth.verifyOtp({
                        phone: finalIdentifier,
                        token: code,
                        type: "sms",
                    })
                    : await supabase.auth.verifyOtp({
                        email: finalIdentifier,
                        token: code,
                        type: "email",
                    });

            if (otpError) throw otpError;
            if (!verifyData?.session)
                throw new Error("Session not established after verification.");

            console.log("✅ OTP verified. Session:", verifyData.session);

            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();
            if (userError) throw userError;
            if (!user) throw new Error("User not found after verification.");

            const email = user.email ?? null;
            const phone = user.phone ?? null;

            // Fetch candidate
            let query = supabase
                .from("candidates")
                .select(`
                    id,
                    full_name,
                    mugshot_url,
                    embedding_vector,
                    candidateassignments(test_id)
                `);

            if (email && phone) {
                query = query.or(
                    `email.eq.${encodeURIComponent(email)},phone.eq.${encodeURIComponent(phone)}`
                );
            } else if (email) {
                query = query.eq("email", email);
            } else if (phone) {
                query = query.eq("phone", phone);
            } else {
                throw new Error("No valid identifier for candidate lookup.");
            }

            const { data: candidate, error: lookupError } = await query.single();
            if (lookupError || !candidate) {
                console.warn("⚠️ Candidate not found:", lookupError);
                navigate("/candidate/unauthorized");
                return;
            }

            const assignment = candidate.candidateassignments?.[0];
            const missingEmbedding =
                !candidate.embedding_vector ||
                (Array.isArray(candidate.embedding_vector) &&
                    candidate.embedding_vector.length === 0);

            // Redirect logic
            if (!candidate.mugshot_url || missingEmbedding) {
                navigate("/candidate/enroll", { state: { candidateId: candidate.id } });
            } else if (assignment?.test_id) {
                navigate("/candidate/profile", {
                    state: {
                        candidateId: candidate.id,
                        testId: assignment.test_id,
                    },
                });
            } else {
                navigate("/candidate/notest");
            }
        } catch (err: any) {
            console.error("❌ Verification error:", err);
            setMessage("❌ " + (err.message || "Verification failed."));
        }

        setLoading(false);
    }

    return (
        <div className="max-w-md mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4 text-center">Enter Verification Code</h1>

            <form onSubmit={handleVerify} className="space-y-4 relative">
                <input
                    type="text"
                    placeholder="6-digit code"
                    className="w-full border px-3 py-2 rounded text-center text-xl tracking-widest"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={6}
                    inputMode="numeric"
                    disabled={loading}
                />

                <button
                    type="submit"
                    className="w-full bg-indigo-600 text-white py-2 rounded flex justify-center items-center"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="animate-spin mr-2 h-5 w-5" />
                            Verifying...
                        </>
                    ) : (
                        "Verify Code"
                    )}
                </button>
            </form>

            {/* Resend Code button */}
            <div className="mt-4 text-center">
                <button
                    onClick={handleResend}
                    disabled={resending}
                    className={`inline-flex items-center text-sm px-3 py-1 rounded-md transition ${resending
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-indigo-600 hover:text-indigo-800"
                        }`}
                >
                    <RefreshCw
                        className={`mr-1 h-4 w-4 ${resending ? "animate-spin" : ""}`}
                    />
                    {resending ? "Resending..." : "Resend Code"}
                </button>
            </div>

            {message && (
                <p className="mt-4 text-sm text-center text-gray-700 transition-opacity duration-300">
                    {message}
                </p>
            )}
        </div>
    );
}
